import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getAsrCorrectionConfigApi,
  updateAsrCorrectionConfigApi,
} from '@/lib/reports-api';
import type { AsrCorrectionConfig } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #291 — 管理员「系统设置 → AI文本分析 → AI文本纠错」。
 *
 * 真实 GET/PATCH /system/asr-correction-config（ADMIN+，按 orgId 隔离）。配置 SOP/质检
 * ASR 完成后、AI 评分前执行的 DeepSeek 严格纠错：开关 / 模型 / 提示词 / temperature（默认
 * 0.1，最大 0.2）/ max_tokens。运行时即时生效，无需重启。纠错 prompt 与质检分析 prompt
 * 语义分离，绝不复用。
 */
// Issue #343：DeepSeek 可配置模型白名单（与后端 org-config.DEEPSEEK_MODEL_VALUES 对齐）。
const DEEPSEEK_MODEL_OPTIONS = [
  { value: 'deepseek-v4-pro', label: 'deepseek-v4-pro（默认，质量优先）' },
  { value: 'deepseek-v4-flash', label: 'deepseek-v4-flash（低延迟）' },
] as const;

const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

interface Draft {
  enabled: boolean;
  model: string;
  prompt: string;
  temperature: string;
  maxTokens: string;
  // Issue #373：术语纠错规则原文（多行）。
  termRules: string;
}

function toDraft(c: AsrCorrectionConfig): Draft {
  return {
    enabled: c.enabled,
    model: c.model,
    prompt: c.prompt ?? '',
    temperature: String(c.temperature),
    maxTokens: String(c.maxTokens),
    termRules: c.termRules ?? '',
  };
}

export function AsrCorrectionSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'asr-correction-config'],
    queryFn: getAsrCorrectionConfigApi,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.config) setDraft(toDraft(query.data.config));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: Partial<AsrCorrectionConfig>) => updateAsrCorrectionConfigApi(body),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(toDraft(data.config));
      await queryClient.invalidateQueries({ queryKey: ['system', 'asr-correction-config'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError ? `${err.message}（HTTP ${err.status}）` : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title="AI文本纠错">
        <LoadingState label="正在加载 AI 文本纠错配置…" />
      </Card>
    );
  }
  if (query.isError || !draft) {
    return (
      <Card title="AI文本纠错">
        <ErrorState
          title="加载 AI 文本纠错配置失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const range = query.data?.range;
  const tempMax = range?.temperatureMax ?? 0.2;
  const tempMin = range?.temperatureMin ?? 0;
  const tokMin = range?.maxTokensMin ?? 1024;
  const tokMax = range?.maxTokensMax ?? 999999;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const temperature = Number(draft.temperature);
  const maxTokens = Number(draft.maxTokens);
  const localInvalid =
    !Number.isFinite(temperature) ||
    temperature < tempMin ||
    temperature > tempMax ||
    !Number.isFinite(maxTokens) ||
    maxTokens < tokMin ||
    maxTokens > tokMax ||
    draft.model.trim().length === 0;

  const promptValue = draft.prompt ?? '';
  const defaultPrompt = query.data?.defaults?.prompt ?? '';

  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    mutation.mutate({
      enabled: draft.enabled,
      model: draft.model.trim(),
      prompt: promptValue,
      temperature,
      maxTokens,
      // Issue #373：术语纠错规则原文（多行）随同一次真实 PATCH 保存，非前端本地态。
      termRules: draft.termRules,
    });
  };

  return (
    <Card
      title="AI文本纠错"
      description="SOP 培训 / 通话质检 ASR 完成后、AI 评分前，对转写文本做 DeepSeek 严格纠错（只修误识别/术语/标点/错别字，不扩写、不改意图）。纠错后文本进入评分与展示，原文保留审计。保存后运行时即时生效，无需重启。未配置 DeepSeek 密钥时生产环境会明确失败，不返回假纠错。"
    >
      <label className="mb-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={draft.enabled}
          disabled={!canEdit}
          onChange={(e) => set('enabled', e.target.checked)}
          data-testid="asr-correction-enabled"
        />
        <span className="text-slate-700">启用 ASR 文本纠错</span>
        <span className="text-xs text-slate-400">（关闭后评分直接使用原始转写，仍保留原文审计）</span>
      </label>

      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="asr-correction-model">
          纠错模型
        </label>
        {/* Issue #343：模型从自由输入收敛为可控选项（deepseek-v4-pro / deepseek-v4-flash）。 */}
        <select
          id="asr-correction-model"
          className="block w-72 max-w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50"
          value={DEEPSEEK_MODEL_OPTIONS.some((o) => o.value === draft.model) ? draft.model : 'deepseek-v4-pro'}
          disabled={!canEdit}
          onChange={(e) => set('model', e.target.value)}
          data-testid="asr-correction-model"
        >
          {DEEPSEEK_MODEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-400">默认 deepseek-v4-pro；如需更低延迟可切换 deepseek-v4-flash。保存后运行时即时生效。</p>
      </div>

      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700" htmlFor="asr-correction-prompt">
            纠错提示词
          </label>
          {canEdit && defaultPrompt && promptValue.trim() !== defaultPrompt.trim() ? (
            <button
              type="button"
              className="text-xs text-brand-600 hover:underline"
              onClick={() => set('prompt', defaultPrompt)}
              data-testid="asr-correction-prompt-reset"
            >
              恢复默认提示词
            </button>
          ) : null}
        </div>
        <textarea
          id="asr-correction-prompt"
          className="block w-full whitespace-pre-wrap break-words rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-slate-400"
          style={{ height: 200 }}
          value={promptValue}
          maxLength={4000}
          disabled={!canEdit}
          placeholder={defaultPrompt}
          onChange={(e) => set('prompt', e.target.value)}
          data-testid="asr-correction-prompt"
        />
        <p className="mt-1 text-xs text-slate-400">
          严格限制：只修正 ASR 误识别 / 术语 / 标点 / 明显错别字；不得扩写、不改医学/销售意图、不补充原文不存在的信息。留空则使用出厂默认。最长 4000 字符。
        </p>
      </div>

      {/* Issue #373：术语纠错规则（结构化注入纠错 prompt，做上下文智能纠错，非全局替换）。 */}
      <div className="mb-4">
        <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="asr-correction-term-rules">
          术语纠错规则
        </label>
        <textarea
          id="asr-correction-term-rules"
          className="block w-full whitespace-pre-wrap break-words rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-slate-400"
          style={{ height: 140 }}
          value={draft.termRules}
          maxLength={4000}
          disabled={!canEdit}
          placeholder={'{金专|金砖|金钻} 表示把“金砖/金钻”按上下文纠为“金专”\n{医能量|一能量|衣能量} 表示把“一能量/衣能量”按上下文纠为“医能量”'}
          onChange={(e) => set('termRules', e.target.value)}
          data-testid="asr-correction-term-rules"
        />
        <p className="mt-1 text-xs text-slate-400">
          每行一条，格式 <code className="rounded bg-slate-100 px-1 py-0.5">{'{标准词|误识别词1|误识别词2}'}</code>：花括号内第一项为标准词，其余为常见 ASR 误识别词，花括号后可写说明。保存后运行时即时生效；模型会按上下文智能纠错（非无条件全局替换）。留空表示不启用术语规则。最长 4000 字符。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Row label="temperature" hint={`默认 0.1，最大 ${tempMax}`}>
          <input
            className={INPUT}
            type="number"
            step={0.05}
            min={tempMin}
            max={tempMax}
            value={draft.temperature}
            disabled={!canEdit}
            onChange={(e) => set('temperature', e.target.value)}
            data-testid="asr-correction-temperature"
          />
        </Row>
        <Row label="max_tokens" hint={`默认 4000，区间 ${tokMin}-${tokMax}`}>
          <input
            className={INPUT}
            type="number"
            min={tokMin}
            max={tokMax}
            value={draft.maxTokens}
            disabled={!canEdit}
            onChange={(e) => set('maxTokens', e.target.value)}
            data-testid="asr-correction-max-tokens"
          />
        </Row>
      </div>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          temperature 需在 {tempMin}-{tempMax}，max_tokens 需在 {tokMin}-{tokMax}，模型不能为空。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button className={PRIMARY_BTN} onClick={onSave} disabled={mutation.isPending || localInvalid}>
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? <span className="text-xs text-emerald-600">已保存，运行时即时生效。</span> : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改 AI 文本纠错配置。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}

function Row({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-600">
        {label}
        <span className="ml-1 text-xs text-slate-400">（{hint}）</span>
      </span>
      {children}
    </label>
  );
}
